import { useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistProfile,
} from "../store/slices/authSlice";
import {
  checkPermission,
  canSendSMS,
  canUseAI,
  canCreateRoom,
  canAddTherapist,
  canCreatePatient,
  canIntegrateMorning,
  canIntegrateGrow,
  canPerformWrite,
  clearUsageCache,
  LIMIT_TYPES,
} from "../services/planService";
import PlanLimitModal from "../components/shared/PlanLimitModal";

/**
 * usePlanPermissions Hook
 *
 * Provides easy access to plan permission checks with built-in modal handling.
 *
 * Usage:
 * ```jsx
 * const { checkBeforeAction, PlanLimitModalComponent } = usePlanPermissions();
 *
 * const handleCreatePatient = async () => {
 *   const canProceed = await checkBeforeAction(LIMIT_TYPES.PATIENTS);
 *   if (!canProceed) return; // Modal is shown automatically
 *
 *   // Continue with patient creation...
 * };
 *
 * return (
 *   <>
 *     <button onClick={handleCreatePatient}>צור מטופל</button>
 *     <PlanLimitModalComponent />
 *   </>
 * );
 * ```
 */
function usePlanPermissions() {
  const clinicId = useSelector(selectClinicId);
  const therapistProfile = useSelector(selectTherapistProfile);

  // Modal state
  const [modalState, setModalState] = useState({
    isOpen: false,
    limitType: null,
    current: undefined,
    limit: undefined,
    plan: "trial",
  });

  // Loading state for checks
  const [isChecking, setIsChecking] = useState(false);

  // Get therapist name for modal messages
  const therapistName = therapistProfile
    ? `${therapistProfile.firstName || ""} ${therapistProfile.lastName || ""}`.trim()
    : null;

  /**
   * Close the modal
   */
  const closeModal = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  /**
   * Show the limit modal with specific limit info
   */
  const showLimitModal = useCallback((limitInfo) => {
    setModalState({
      isOpen: true,
      limitType: limitInfo.limitType,
      current: limitInfo.current,
      limit: limitInfo.limit,
      plan: limitInfo.plan || "trial",
    });
  }, []);

  /**
   * Generic permission check before an action
   * Returns true if allowed, false if blocked (modal shown)
   *
   * @param {string} limitType - One of LIMIT_TYPES
   * @param {boolean} showModal - Whether to show modal if blocked (default: true)
   * @returns {Promise<boolean>} - true if allowed, false if blocked
   */
  const checkBeforeAction = useCallback(
    async (limitType, showModal = true) => {
      if (!clinicId) {
        console.warn("[usePlanPermissions] No clinicId available");
        return true; // Fail open
      }

      setIsChecking(true);

      try {
        const result = await checkPermission(clinicId, limitType);

        if (!result.allowed && showModal) {
          showLimitModal(result);
        }

        return result.allowed;
      } catch (error) {
        console.error("[usePlanPermissions] Error checking permission:", error);
        return true; // Fail open on error
      } finally {
        setIsChecking(false);
      }
    },
    [clinicId, showLimitModal]
  );

  /**
   * Specific check functions for convenience
   */
  const checkSMS = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.SMS, showModal),
    [checkBeforeAction]
  );

  const checkAI = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.AI_ACTIONS, showModal),
    [checkBeforeAction]
  );

  const checkRoom = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.ROOMS, showModal),
    [checkBeforeAction]
  );

  const checkTherapist = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.THERAPISTS, showModal),
    [checkBeforeAction]
  );

  const checkPatient = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.PATIENTS, showModal),
    [checkBeforeAction]
  );

  const checkMorning = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.MORNING_INTEGRATION, showModal),
    [checkBeforeAction]
  );

  const checkDigitalInvoice = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.DIGITAL_INVOICE_INTEGRATION, showModal),
    [checkBeforeAction]
  );

  const checkWrite = useCallback(
    (showModal = true) => checkBeforeAction(LIMIT_TYPES.WRITE_OPS, showModal),
    [checkBeforeAction]
  );

  /**
   * Refresh the usage cache
   * Call after actions that modify usage (e.g., after sending SMS)
   */
  const refreshUsageCache = useCallback(() => {
    if (clinicId) {
      clearUsageCache(clinicId);
    }
  }, [clinicId]);

  /**
   * The Modal Component - render this in your component
   */
  const PlanLimitModalComponent = useCallback(
    () => (
      <PlanLimitModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        limitType={modalState.limitType}
        current={modalState.current}
        limit={modalState.limit}
        plan={modalState.plan}
        therapistName={therapistName}
      />
    ),
    [modalState, closeModal, therapistName]
  );

  return {
    // Generic check function
    checkBeforeAction,

    // Specific check functions
    checkSMS,
    checkAI,
    checkRoom,
    checkTherapist,
    checkPatient,
    checkMorning,
    checkDigitalInvoice,
    checkWrite,

    // States
    isChecking,
    isModalOpen: modalState.isOpen,

    // Modal controls
    closeModal,
    showLimitModal,

    // Cache management
    refreshUsageCache,

    // Modal component
    PlanLimitModalComponent,

    // Export LIMIT_TYPES for convenience
    LIMIT_TYPES,
  };
}

export default usePlanPermissions;

// Also export the individual check functions for use outside of React components
export {
  canSendSMS,
  canUseAI,
  canCreateRoom,
  canAddTherapist,
  canCreatePatient,
  canIntegrateMorning,
  canIntegrateGrow,
  canPerformWrite,
  clearUsageCache,
  LIMIT_TYPES,
};
